import React, { useState, useRef } from "react";
import { useAtom } from "jotai";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    Grid,
    List,
    ListItem,
    Button,
    Typography,
    useTheme,
    ThemeProvider,
    DialogActions
} from "@mui/material";
import { mbxUserAtom, isMbxUserVerifiedStaff } from "../atoms/atoms";
import ExportDateRangePicker from "../utils/CommonComonents/ExportDateRangePicker";
import { execReadBySortkeyFromToken } from "../modules/DBService";
import { ordersByCompanyCreatedAt } from "../graphql/queries";
import { CSVLink } from "react-csv";
import moment from "moment";
import { subDays } from "date-fns";
import { DialogTheme } from "../styles/DialogTheme";
import LoadingButton from "@mui/lab/LoadingButton";
import CircleIcon from "@mui/icons-material/Circle";

const csvHeaders = [
    { label: "ID", key: "ID" },
    { label: "Create Date", key: "CREATEDAT" },
    { label: "Order No", key: "ORDER_NO" },
    { label: "Service Chosen", key: "SERVICECHOSEN" },
    { label: "Status", key: "STATUS" },
    { label: "Client Name", key: "CLIENTNAME" },
    { label: "Booking Address", key: "BOOKINGADDRESS" },
    { label: "Provider Name", key: "PROVIDERNAME" },
    { label: "Credits Left", key: "CREDITSLEFT" },
    { label: "Subtotal", key: "SUBTOTAL" },
    { label: "Service Fee", key: "SERVICEFEE" },
    { label: "Tax", key: "TAX" },
    { label: "Total", key: "TOTAL" },
    { label: "Misc Charges", key: "MISC_CHARGES" },
    { label: "Additional Charges", key: "ADDITIONAL_CHARGES" },
    { label: "Currency", key: "CURRENCY" }
];

function supplyCSVData(item) {
    const {
        id: ID,
        createdAtValue: CREATEDAT,
        orderNo: ORDER_NO,
        orderSummary: ORDERSUMMARY,
        status: STATUS,
        clientName: CLIENTNAME,
        addressoneline: BOOKINGADDRESS,
        providerName: PROVIDERNAME,
        credits: CREDITSLEFT,
        subtotal: SUBTOTAL,
        servicechargeamt: SERVICEFEE,
        taxamt: TAX,
        total: TOTAL,
        miscChargesString: MISC_CHARGES,
        additionalChargesString: ADDITIONAL_CHARGES,
        currencyValue: CURRENCY
    } = item;
    const newItem = {
        ID,
        CREATEDAT,
        ORDER_NO,
        SERVICECHOSEN: JSON.parse(ORDERSUMMARY || "{}")?.rows?.[0]?.service,
        STATUS,
        CLIENTNAME,
        BOOKINGADDRESS,
        PROVIDERNAME,
        CREDITSLEFT,
        SUBTOTAL: Number(SUBTOTAL).toFixed(2),
        SERVICEFEE: Number(SERVICEFEE).toFixed(2),
        TAX: Number(TAX).toFixed(2),
        TOTAL: Number(TOTAL).toFixed(2),
        MISC_CHARGES,
        ADDITIONAL_CHARGES,
        CURRENCY
    };
    return newItem;
}

// Helper to shape display fields for order
function supplyData(item) {
    item.companyName =
        item.company && item.company.name ? item.company.name : "";
    item.providerName =
        item.provider && item.provider.firstname
            ? `${item.provider.firstname} ${item.provider.lastname}`
            : "";
    item.clientName = item.client
        ? `${
              item.client && item.client.user ? item.client.user.firstname : ""
          } ${item.client && item.client.user ? item.client.user.lastname : ""}`
        : "";
    item.addressoneline = item.bookingAddress
        ? JSON.parse(item.bookingAddress).addrOneLine
        : "";
    item.credits = item.clientpackage
        ? (
              item.clientpackage.initialQuantity -
              item.clientpackage.usedQuantity
          ).toString()
        : "N/A";
    const orderSummary = JSON.parse(
        item.orderSummary ? item.orderSummary : "{}"
    );
    item.miscChargesString = orderSummary.miscBillingTotal
        ? orderSummary.miscBillingTotal
        : "0";
    item.additionalChargesString = orderSummary.additionalChargesTotal
        ? orderSummary.additionalChargesTotal
        : "0";
    item.currencyValue = item.currency;
    return item;
}

function ExportOrderList({
    open,
    onClose,
    selectedClient,
    selectedClientId,
    selectedProvider,
    selectedOrderNumber
}) {
    // Dates default: from today, to 15 days from now
    const [exportFromDate, setExportFromDate] = useState(
        subDays(new Date(), 15)
    );
    const [exportToDate, setExportToDate] = useState(new Date());
    const [preparingExportData, setPreparingExportData] = useState(false);
    const [exportReadyOrders, setExportReadyOrders] = useState([]);
    const [noOrdersMessage, setNoOrdersMessage] = useState(false);
    const csvLink = useRef(null);
    const [mbxUser] = useAtom(mbxUserAtom);
    const [isVerifiedStaffLoggedIn] = useAtom(isMbxUserVerifiedStaff);
    const theme = useTheme();

    function makeFilter(
        selectedClient,
        selectedClientId,
        selectedProvider,
        selectedOrderNumber
    ) {
        const filter = [];

        // Client filter
        if (selectedClient && selectedClientId) {
            filter.push({ clientId: { eq: selectedClientId } });
        } else if (!isVerifiedStaffLoggedIn) {
            filter.push({ clientId: { eq: mbxUser.id } });
        }

        // Provider filter
        if (selectedProvider && selectedProvider.id) {
            filter.push({ providerId: { eq: selectedProvider.id } });
        }

        // Order number filter
        if (selectedOrderNumber) {
            filter.push({ orderNo: { eq: selectedOrderNumber } });
        }

        return filter.length > 0 ? { and: filter } : null;
    }

    // Helper to get correct query operation
    async function getExportOperation({
        selectedClient,
        selectedClientId,
        selectedProvider,
        selectedOrderNumber,
        exportFromDate,
        exportToDate
    }) {
        return {
            opname: "ordersByCompanyCreatedAt",
            op: ordersByCompanyCreatedAt,
            id: { companyId: mbxUser.companyId },
            skey: {
                createdAt: {
                    between: [
                        exportFromDate instanceof Date
                            ? exportFromDate.toISOString()
                            : new Date(exportFromDate).toISOString(),
                        exportToDate instanceof Date
                            ? exportToDate.toISOString()
                            : new Date(exportToDate).toISOString()
                    ]
                }
            },
            filter: makeFilter(
                selectedClient,
                selectedClientId,
                selectedProvider,
                selectedOrderNumber,
                exportFromDate,
                exportToDate
            ),
            sortDirection: "DESC"
        };
    }

    // Fetch and export data
    const handleExport = async () => {
        setExportReadyOrders([]);
        setPreparingExportData(true);

        const op = await getExportOperation({
            selectedClient,
            selectedClientId,
            selectedProvider,
            selectedOrderNumber,
            exportFromDate,
            exportToDate
        });

        let allOrders = [];
        let token;
        do {
            const data = await execReadBySortkeyFromToken(op, token, []);
            allOrders = allOrders.concat(data.items || []);
            token = data.token;
        } while (token);

        // Format for CSV
        let rows = [];
        if (allOrders && allOrders.length > 0) {
            allOrders.forEach((item) => {
                supplyData(item);
                item.createdAtValue = moment(item.createdAt).format(
                    "YYYY-MM-DD hh:mm A"
                );
                rows.push(item);
            });
            const csvData = rows.map((item) => supplyCSVData(item));
            setExportReadyOrders(csvData);
            setPreparingExportData(false);
            if (csvLink.current) {
                setTimeout(() => csvLink.current.link.click(), 100);
            }
        } else {
            setPreparingExportData(false);
            setNoOrdersMessage(true);
            return;
        }
    };

    const handleClose = () => {
        setNoOrdersMessage(false);
        setExportReadyOrders([]);
        setPreparingExportData(false);
        if (onClose) onClose();
    };

    return (
        <ThemeProvider theme={DialogTheme(theme)}>
            <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
                <DialogTitle sx={{ paddingBottom: "12px" }}>
                    Export Orders
                </DialogTitle>
                <DialogContent>
                    <Grid container spacing={2.25}>
                        <Grid item xs={12}>
                            <Typography>
                                Please confirm the following filter criteria:
                            </Typography>
                            {!selectedClient &&
                                !selectedProvider &&
                                !selectedOrderNumber && (
                                    <ListItem>
                                        <CircleIcon
                                            sx={{
                                                width: "0.25rem",
                                                height: "0.25rem",
                                                color: theme.palette.text
                                                    .secondary
                                            }}
                                        />
                                        <Typography>{`All Orders`}</Typography>
                                    </ListItem>
                                )}
                            <List>
                                {selectedClient && (
                                    <ListItem>
                                        <CircleIcon
                                            sx={{
                                                width: "0.25rem",
                                                height: "0.25rem",
                                                color: theme.palette.text
                                                    .secondary
                                            }}
                                        />
                                        <Typography>
                                            {`Client Name: ${selectedClient.firstname} ${selectedClient.lastname}`}
                                        </Typography>
                                    </ListItem>
                                )}
                                {selectedProvider && (
                                    <ListItem>
                                        <CircleIcon
                                            sx={{
                                                width: "0.25rem",
                                                height: "0.25rem",
                                                color: theme.palette.text
                                                    .secondary
                                            }}
                                        />
                                        <Typography>
                                            {`Provider Name: ${selectedProvider.firstname} ${selectedProvider.lastname}`}
                                        </Typography>
                                    </ListItem>
                                )}
                                {selectedOrderNumber && (
                                    <ListItem>
                                        <CircleIcon
                                            sx={{
                                                width: "0.25rem",
                                                height: "0.25rem",
                                                color: theme.palette.text
                                                    .secondary
                                            }}
                                        />
                                        <Typography>
                                            {`Order Number: ${selectedOrderNumber}`}
                                        </Typography>
                                    </ListItem>
                                )}
                            </List>
                        </Grid>
                        <Grid item xs={12}>
                            <Typography>Please select a date range</Typography>
                            <ExportDateRangePicker
                                setFrom={(date) => {
                                    setExportFromDate(date);
                                    setNoOrdersMessage(false);
                                }}
                                setTo={(date) => {
                                    setExportToDate(date);
                                    setNoOrdersMessage(false);
                                }}
                                from={exportFromDate}
                                to={exportToDate}
                            />
                        </Grid>
                        {noOrdersMessage && (
                            <Grid
                                variant="errorTextContainer"
                                item
                                xs={12}
                                sm={12}
                            >
                                <Typography variant="errorText">
                                    No orders were found for your selected
                                    criteria. Please adjust your filters or
                                    select a different date range.
                                </Typography>
                            </Grid>
                        )}
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Grid display={"flex"} gap={"18px"}>
                        <Button
                            onClick={handleClose}
                            variant="cancelButton"
                            disabled={preparingExportData}
                        >
                            Cancel
                        </Button>

                        <LoadingButton
                            color="primary"
                            variant="contained"
                            loading={preparingExportData}
                            onClick={handleExport}
                            type="submit"
                        >
                            Export
                        </LoadingButton>
                    </Grid>
                    <CSVLink
                        headers={csvHeaders}
                        data={exportReadyOrders}
                        filename={`orders_${moment(Date.now()).format(
                            "YYYY-MM-DD"
                        )}.csv`}
                        ref={csvLink}
                    ></CSVLink>
                </DialogActions>
            </Dialog>
        </ThemeProvider>
    );
}

export default ExportOrderList;
